import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlus,
  KeyRound,
  Power,
  ArrowLeft,
  X,
  ChevronRight
} from 'lucide-react';
import { useLab } from '../LabContext';
import { LabLayout, Card } from '../LabUI';

// API base mirrors LabContext's resolver but for /api/lab/admin.
const adminBase = () => {
  const path = '/api/lab/admin';
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) {
    /* fall through */
  }
  return path;
};

const apiListStudents = async () => {
  const r = await fetch(`${adminBase()}/students`, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to load');
  return r.json();
};

const apiCreateStudent = async (payload) => {
  const r = await fetch(`${adminBase()}/students`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = j.detail;
    if (Array.isArray(detail)) {
      throw new Error(detail.map((d) => d.msg).join('; '));
    }
    throw new Error(detail || 'Failed to create');
  }
  return j;
};

const apiPatchStudent = async (studentId, patch) => {
  const r = await fetch(`${adminBase()}/students/${studentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch)
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to update');
  return r.json();
};

const apiResetPassword = async (studentId, newPassword) => {
  const r = await fetch(`${adminBase()}/students/${studentId}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ newPassword })
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = j.detail;
    if (Array.isArray(detail)) throw new Error(detail.map((d) => d.msg).join('; '));
    throw new Error(detail || 'Failed to reset');
  }
  return j;
};

// ---------- Create child form ----------
const CreateChildForm = ({ language, onCreated }) => {
  const [name, setName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError(language === 'ru' ? 'Введите имя.' : 'Enter the name.');
      return;
    }
    if (!loginId.trim()) {
      setError(language === 'ru' ? 'Введите логин.' : 'Enter the login.');
      return;
    }
    if (password.length < 8) {
      setError(
        language === 'ru'
          ? 'Пароль должен быть не короче 8 символов.'
          : 'Password must be at least 8 characters.'
      );
      return;
    }
    if (password !== password2) {
      setError(language === 'ru' ? 'Пароли не совпадают.' : 'Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const created = await apiCreateStudent({
        name: name.trim(),
        login: loginId.trim().toLowerCase(),
        password,
        active: true
      });
      setSuccess(
        language === 'ru'
          ? `Аккаунт «${created.name}» создан. Передайте пароль ребёнку — здесь он больше не показывается.`
          : `Account "${created.name}" created. Share the password with the child — it will not be shown again here.`
      );
      setName('');
      setLoginId('');
      setPassword('');
      setPassword2('');
      onCreated && onCreated(created);
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="parent-users-create-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          label={language === 'ru' ? 'Имя ребёнка' : 'Child display name'}
          value={name}
          onChange={setName}
          testid="parent-users-create-name"
          placeholder={language === 'ru' ? 'Например: Лиза' : 'e.g., Lisa'}
        />
        <Field
          label={language === 'ru' ? 'Логин' : 'Login ID'}
          value={loginId}
          onChange={(v) => setLoginId(v.toLowerCase())}
          testid="parent-users-create-login"
          placeholder={language === 'ru' ? 'строчные буквы и цифры' : 'lowercase, digits'}
        />
        <Field
          label={language === 'ru' ? 'Пароль' : 'Password'}
          value={password}
          onChange={setPassword}
          type="password"
          testid="parent-users-create-password"
          placeholder={language === 'ru' ? 'минимум 8 символов' : 'min 8 chars'}
        />
        <Field
          label={language === 'ru' ? 'Повтор пароля' : 'Repeat password'}
          value={password2}
          onChange={setPassword2}
          type="password"
          testid="parent-users-create-password2"
          placeholder={language === 'ru' ? 'ещё раз пароль' : 'repeat password'}
        />
      </div>

      {error && (
        <div className="text-red-300/90 text-sm font-sans" data-testid="parent-users-create-error">
          {error}
        </div>
      )}
      {success && (
        <div
          className="text-emerald-300/90 text-sm font-sans"
          data-testid="parent-users-create-success"
        >
          {success}
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="parent-users-create-submit"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 disabled:cursor-not-allowed text-[#1a1208] font-sans transition-colors"
      >
        <UserPlus className="w-4 h-4" />
        {busy
          ? language === 'ru'
            ? 'Создаём…'
            : 'Creating…'
          : language === 'ru'
            ? 'Создать аккаунт ребёнка'
            : 'Create child account'}
      </button>
    </form>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-amber-200/60 font-sans">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      autoComplete="off"
      autoCapitalize="none"
      spellCheck="false"
      className="mt-1 w-full px-3 py-2 rounded-xl bg-[#0c1230]/60 border border-amber-300/15 text-[#f5ebe0] placeholder:text-amber-200/30 focus:outline-none focus:border-amber-300/40 font-sans text-base"
    />
  </label>
);

// ---------- Reset password modal ----------
const ResetPasswordModal = ({ student, language, onClose, onDone }) => {
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!student) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(
        language === 'ru'
          ? 'Пароль должен быть не короче 8 символов.'
          : 'Password must be at least 8 characters.'
      );
      return;
    }
    if (password !== password2) {
      setError(language === 'ru' ? 'Пароли не совпадают.' : 'Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await apiResetPassword(student.id, password);
      onDone &&
        onDone(
          language === 'ru'
            ? 'Пароль обновлён. Старый пароль больше не работает.'
            : 'Password updated. The old password no longer works.'
        );
      onClose();
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-[2px]"
      onClick={onClose}
      data-testid="parent-users-reset-overlay"
    >
      <Card
        className="w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-amber-200/65 text-xs uppercase tracking-wider font-sans">
              {language === 'ru' ? 'Сброс пароля' : 'Reset password'}
            </div>
            <div
              className="text-xl text-[#f3d9a4] mt-0.5"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid="parent-users-reset-title"
            >
              {student.name}
              <span className="ml-2 text-sm text-amber-200/55 font-sans">
                ({student.login})
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full border border-amber-300/20 text-amber-200/80 hover:bg-amber-300/5"
            data-testid="parent-users-reset-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field
            label={language === 'ru' ? 'Новый пароль' : 'New password'}
            value={password}
            onChange={setPassword}
            type="password"
            testid="parent-users-reset-password"
          />
          <Field
            label={language === 'ru' ? 'Повтор нового пароля' : 'Repeat new password'}
            value={password2}
            onChange={setPassword2}
            type="password"
            testid="parent-users-reset-password2"
          />
          {error && (
            <div
              className="text-red-300/90 text-sm font-sans"
              data-testid="parent-users-reset-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="parent-users-reset-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 text-[#1a1208] font-sans"
          >
            <KeyRound className="w-4 h-4" />
            {busy
              ? language === 'ru'
                ? 'Сохраняем…'
                : 'Saving…'
              : language === 'ru'
                ? 'Обновить пароль'
                : 'Update password'}
          </button>
        </form>
      </Card>
    </div>
  );
};

// ---------- Page ----------
const ParentUsers = () => {
  const { language } = useLab();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalSuccess, setGlobalSuccess] = useState('');
  const [resetTarget, setResetTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await apiListStudents();
      setStudents(data.students || []);
    } catch (e) {
      setError(e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleActive = async (student) => {
    try {
      await apiPatchStudent(student.id, { active: !student.active });
      setGlobalSuccess(
        student.active
          ? language === 'ru'
            ? 'Аккаунт деактивирован.'
            : 'Account deactivated.'
          : language === 'ru'
            ? 'Аккаунт снова активен.'
            : 'Account reactivated.'
      );
      reload();
    } catch (e) {
      setError(e.message || 'Failed');
    }
  };

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [students]
  );

  return (
    <LabLayout>
      <div className="mt-2 flex items-center gap-2">
        <Link
          to="/lab/parent"
          className="inline-flex items-center gap-1.5 text-amber-200/70 text-sm font-sans hover:text-amber-200"
          data-testid="parent-users-back"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'ru' ? 'Кабинет родителя' : 'Parent dashboard'}
        </Link>
      </div>

      <section className="mt-3">
        <h1
          className="text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="parent-users-title"
        >
          {language === 'ru' ? 'Дети и аккаунты' : 'Students & Accounts'}
        </h1>
        <p className="mt-1 text-amber-200/65 font-serif">
          {language === 'ru'
            ? 'Создавайте аккаунты для детей и управляйте паролями вашей семьи.'
            : 'Create child accounts and manage passwords for your family.'}
        </p>
      </section>

      {globalSuccess && (
        <div
          className="mt-4 rounded-xl border border-emerald-300/25 bg-emerald-400/10 text-emerald-200 text-sm px-4 py-2.5 font-sans"
          data-testid="parent-users-global-success"
        >
          {globalSuccess}
        </div>
      )}
      {error && (
        <div
          className="mt-4 rounded-xl border border-red-300/25 bg-red-400/10 text-red-200 text-sm px-4 py-2.5 font-sans"
          data-testid="parent-users-global-error"
        >
          {error}
        </div>
      )}

      {/* Existing students list */}
      <Card className="mt-5 p-4">
        <h2
          className="text-base text-[#f3d9a4] mb-3"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {language === 'ru' ? 'Дети в вашей семье' : 'Children in your family'}
        </h2>
        {loading ? (
          <div className="text-amber-200/55 text-sm font-sans" data-testid="parent-users-loading">
            {language === 'ru' ? 'Загрузка…' : 'Loading…'}
          </div>
        ) : sortedStudents.length === 0 ? (
          <div className="text-amber-200/55 text-sm font-sans" data-testid="parent-users-empty">
            {language === 'ru'
              ? 'Пока нет детских аккаунтов. Создайте первый ниже.'
              : 'No child accounts yet. Create the first one below.'}
          </div>
        ) : (
          <ul className="divide-y divide-amber-300/10" data-testid="parent-users-list">
            {sortedStudents.map((s) => (
              <li
                key={s.id}
                className="py-3 flex items-center gap-3"
                data-testid={`parent-users-row-${s.login}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[#f5ebe0]/95 font-sans text-base flex items-center gap-2">
                    <span data-testid={`parent-users-row-${s.login}-name`}>{s.name}</span>
                    {!s.active && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-300/20 text-amber-200/60 font-sans"
                        data-testid={`parent-users-row-${s.login}-inactive`}
                      >
                        {language === 'ru' ? 'неактивен' : 'inactive'}
                      </span>
                    )}
                  </div>
                  <div className="text-amber-200/55 text-xs font-mono mt-0.5">
                    {s.login} · studentId: {s.studentId} ·{' '}
                    {language === 'ru' ? 'курс' : 'course'}: {s.courseTemplate || '—'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setResetTarget(s)}
                  className="p-2 rounded-full border border-amber-300/20 text-amber-200/80 hover:bg-amber-300/5"
                  data-testid={`parent-users-row-${s.login}-reset`}
                  title={language === 'ru' ? 'Сбросить пароль' : 'Reset password'}
                >
                  <KeyRound className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(s)}
                  className={
                    'p-2 rounded-full border transition-colors ' +
                    (s.active
                      ? 'border-amber-300/20 text-amber-200/80 hover:bg-amber-300/5'
                      : 'border-emerald-300/30 text-emerald-200 hover:bg-emerald-400/10')
                  }
                  data-testid={`parent-users-row-${s.login}-toggle-active`}
                  title={
                    s.active
                      ? language === 'ru'
                        ? 'Деактивировать'
                        : 'Deactivate'
                      : language === 'ru'
                        ? 'Активировать'
                        : 'Reactivate'
                  }
                >
                  <Power className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Create new child */}
      <Card className="mt-4 p-4">
        <h2
          className="text-base text-[#f3d9a4] mb-3"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="parent-users-create-heading"
        >
          {language === 'ru' ? 'Создать аккаунт ребёнка' : 'Create child account'}
        </h2>
        <CreateChildForm language={language} onCreated={() => reload()} />
      </Card>

      {/* Self-service: parent's own password */}
      <Card className="mt-4 p-4">
        <Link
          to="/lab/parent/account"
          className="flex items-center gap-3 text-[#f5ebe0]/90"
          data-testid="parent-users-change-own-password-link"
        >
          <span className="w-9 h-9 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
            <KeyRound className="w-[16px] h-[16px]" />
          </span>
          <span className="flex-1 font-sans text-sm">
            {language === 'ru' ? 'Изменить мой пароль' : 'Change my password'}
          </span>
          <ChevronRight className="w-4 h-4 text-amber-200/45" />
        </Link>
      </Card>

      <ResetPasswordModal
        student={resetTarget}
        language={language}
        onClose={() => setResetTarget(null)}
        onDone={(msg) => setGlobalSuccess(msg)}
      />
    </LabLayout>
  );
};

export default ParentUsers;
